"use client";

import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { cn } from "../../utils/cn"; // adjust path if needed

/* ------------------ TypingText Component ------------------ */
export const TypingText = ({
  children,
  as: Component = "div",
  className = "",
  delay = 0,
  duration = 2,
  fontSize = "text-4xl",
  fontWeight = "font-bold",
  letterSpacing = "tracking-wide",
  align = "left",
  loop = false,
}) => {
  const [charIndex, setCharIndex] = useState(0);

  // Split children into letters but preserve styling
  const processChildren = (node) => {
    if (typeof node === "string") {
      return node.split("").map((char, i) => ({
        char: char === " " ? "\u00A0" : char,
        className: "",
        key: `text-${i}`,
      }));
    }

    if (React.isValidElement(node)) {
      const inner = processChildren(node.props.children);
      return inner.map((item) => ({
        ...item,
        className: cn(node.props.className, item.className),
      }));
    }

    if (Array.isArray(node)) {
      return node.flatMap(processChildren);
    }

    return [];
  };

  const characters = processChildren(children);

  useEffect(() => {
    if (!loop) return;
    if (charIndex < characters.length) {
      const timeout = setTimeout(() => {
        setCharIndex((prev) => prev + 1);
      }, duration * 1000 / characters.length);
      return () => clearTimeout(timeout);
    } else {
      setCharIndex(0);
    }
  }, [charIndex, characters.length, duration, loop]);

  const characterVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: {
        delay: delay + i * (duration / characters.length),
        duration: 0.3,
        ease: "easeInOut",
      },
    }),
  };

  return (
    <Component
      className={cn(
        "inline-flex flex-wrap",
        className,
        fontSize,
        fontWeight,
        letterSpacing,
        align === "center"
          ? "justify-center text-center"
          : align === "right"
          ? "justify-end text-right"
          : "justify-start text-left"
      )}
    >
      <motion.span
        className="inline-block"
        initial="hidden"
        animate="visible"
        role="text"
      >
        {characters.map((item, index) => (
          <motion.span
            key={`${item.char}-${index}`}
            className={cn("inline-block", item.className)}
            variants={characterVariants}
            custom={index}
            initial="hidden"
            animate="visible"
          >
            {item.char}
          </motion.span>
        ))}
      </motion.span>
    </Component>
  );
};
